import { getAuthToken } from '../../auth';
import { showSuccessNotification, showErrorNotification } from '../../../utils/notifications';
import { fillSeoForm, getSeoFormData } from './seo-modal';

/**
 * Загружает SEO-данные с сервера
 * @param {string} apiUrl URL API
 * @param {string} pageTitle Название страницы
 * @returns {Promise<Object>} Promise с данными или пустым объектом
 */
export async function loadSeoData(apiUrl, pageTitle) {
    const token = getAuthToken();

    try {
        const response = await fetch(`${apiUrl}/api/seo/${encodeURIComponent(pageTitle)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

        if (response.ok) {
            const data = await response.json();
            // Заполняем форму данными
            fillSeoForm(data);
            return data;
        } else {
            // Если данных еще нет, возвращаем пустой объект
            console.log('No SEO data found for this page');
            return {};
        }
    } catch (error) {
        console.error('Error loading SEO data:', error);
        return {};
    }
}

/**
 * Сохраняет SEO-данные на сервере
 * @param {string} apiUrl URL API
 * @param {string} pageTitle Название страницы
 * @returns {Promise<Object>} Promise с результатом сохранения
 */
export async function saveSeoData(apiUrl, pageTitle) {
    const token = getAuthToken();
    const formData = getSeoFormData();

    try {
        const response = await fetch(`${apiUrl}/api/seo/${encodeURIComponent(pageTitle)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify(formData)
        });

        if (response.ok) {
            const data = await response.json();
            showSuccessNotification('SEO data saved successfully');

            // Обновляем метатеги на странице
            updateDocumentMetadata(data);

            return data;
        } else {
            throw new Error('Failed to save SEO data');
        }
    } catch (error) {
        console.error('Error saving SEO data:', error);
        showErrorNotification('Error saving SEO data. Please try again.');
        throw error;
    }
}

/**
 * Загружает и применяет SEO-метаданные для текущей страницы
 * @param {string} apiUrl URL API
 */
export async function loadAndApplySeoMetadata(apiUrl) {
    const currentPage = document.body.getAttribute('data-page') || 'Home';

    // Загружаем SEO-данные для текущей страницы
    try {
        const response = await fetch(`${apiUrl}/api/seo/${encodeURIComponent(currentPage)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (response.ok) {
            const data = await response.json();
            // Если есть данные, обновляем метатеги
            updateDocumentMetadata(data);
        } else {
            // Если данных нет, просто игнорируем
            console.log('No SEO data found for this page');
        }
    } catch (error) {
        console.error('Error loading SEO metadata:', error);
    }
}

/**
 * Обновляет метатеги на странице
 * @param {Object} seoData Объект с SEO-данными
 */
export function updateDocumentMetadata(seoData) {
    if (!seoData) return;

    // Обновляем заголовок
    if (seoData.meta_title) {
        document.title = seoData.meta_title;
    }

    // Обновляем мета-описание
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', seoData.meta_description || '');

    // Обновляем ключевые слова
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', seoData.meta_keywords || '');

    // Обновляем robots
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', seoData.is_indexed ? seoData.robots_tag : 'noindex, nofollow');

    // Обновляем canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
    }

    if (seoData.canonical_url) {
        linkCanonical.setAttribute('href', seoData.canonical_url);
    }

    // Обновляем Open Graph метатеги
    updateOpenGraphTags(seoData);

    // Обновляем Twitter Card метатеги
    updateTwitterCardTags(seoData);

    // Обновляем Schema разметку
    updateSchemaMarkup(seoData);
}

/**
 * Обновляет Open Graph метатеги
 * @param {Object} seoData Объект с SEO-данными
 */
export function updateOpenGraphTags(seoData) {
    // OG Title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', seoData.og_title || seoData.meta_title || '');

    // OG Description
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', seoData.og_description || seoData.meta_description || '');

    // OG Image
    if (seoData.og_image) {
        let ogImage = document.querySelector('meta[property="og:image"]');
        if (!ogImage) {
            ogImage = document.createElement('meta');
            ogImage.setAttribute('property', 'og:image');
            document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', seoData.og_image);
    }

    // OG Type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
        ogType = document.createElement('meta');
        ogType.setAttribute('property', 'og:type');
        document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', seoData.og_type || 'website');
}

/**
 * Обновляет Twitter Card метатеги
 * @param {Object} seoData Объект с SEO-данными
 */
export function updateTwitterCardTags(seoData) {
    // Twitter Card
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
        twitterCard = document.createElement('meta');
        twitterCard.setAttribute('name', 'twitter:card');
        document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', seoData.twitter_card || 'summary');

    // Twitter Title
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', seoData.twitter_title || seoData.og_title || seoData.meta_title || '');

    // Twitter Description
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDescription);
    }
    twitterDescription.setAttribute('content', seoData.twitter_description || seoData.og_description || seoData.meta_description || '');

    // Twitter Image
    if (seoData.twitter_image || seoData.og_image) {
        let twitterImage = document.querySelector('meta[name="twitter:image"]');
        if (!twitterImage) {
            twitterImage = document.createElement('meta');
            twitterImage.setAttribute('name', 'twitter:image');
            document.head.appendChild(twitterImage);
        }
        twitterImage.setAttribute('content', seoData.twitter_image || seoData.og_image);
    }
}

/**
 * Обновляет Schema разметку
 * @param {Object} seoData Объект с SEO-данными
 */
export function updateSchemaMarkup(seoData) {
    if (seoData.schema_markup) {
        // Удаляем существующую schema разметку
        const existingSchema = document.querySelector('script[type="application/ld+json"]');
        if (existingSchema) {
            existingSchema.remove();
        }

        // Добавляем новую schema разметку
        const schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        schemaScript.textContent = seoData.schema_markup;
        document.head.appendChild(schemaScript);
    }
}